import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import i18n from '../i18n';
import { LANGUAGE_LOCALE_CODES } from '../lib/localeMatch';
import { setSignedIn, track } from '../lib/analytics';

const AuthContext = createContext(null);

const TOKEN_KEY = 'user_token';

// The UI language follows the signed-in user's native language, overriding whatever was
// auto-detected from the browser before login.
const applyUserLanguage = (user) => {
  const code = user?.nativeLanguage && LANGUAGE_LOCALE_CODES[user.nativeLanguage];
  if (code && code !== i18n.language) {
    i18n.changeLanguage(code);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem(TOKEN_KEY);
  const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
  const removeToken = () => localStorage.removeItem(TOKEN_KEY);

  const fetchUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      applyUserLanguage(response.data.user);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      removeToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (!loading) setSignedIn(!!user);
  }, [loading, user]);

  const startGoogleLogin = async () => {
    try {
      const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      sessionStorage.setItem('googleOAuthState', state);

      const response = await axios.get(`${getApiBaseUrl()}/api/auth/google/url`, {
        params: { state }
      });

      if (response.data.success) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Failed to start Google login:', err);
    }
  };

  const loginWithGoogleCode = async (code) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/google/callback`, { code });

      if (response.data.success) {
        setToken(response.data.token);
        setUser(response.data.user);
        applyUserLanguage(response.data.user);
        setSignedIn(true);
        track('sign_in_completed', { method: 'google', newUser: !!response.data.isNewUser });
        return { success: true, user: response.data.user, isNewUser: response.data.isNewUser || false };
      }
    } catch (err) {
      track('sign_in_failed', { method: 'google' });
      const errorMessage = err.response?.data?.error || 'Google login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signIn = (data, method) => {
    setSignedIn(true);
    track('sign_in_completed', { method, newUser: !!data.isNewUser });
    setToken(data.token);
    setUser(data.user);
    applyUserLanguage(data.user);
    return { success: true, user: data.user, isNewUser: !!data.isNewUser };
  };

  const failure = (err, fallback) => {
    const errorMessage = err.response?.data?.error || fallback;
    setError(errorMessage);
    return { success: false, error: errorMessage, status: err.response?.status };
  };

  // Resolves to { success, sent, newAccount } once a code is emailed, or signs straight in under dev login.
  const requestCode = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login/request`, { email });
      if (response.data.token) return signIn(response.data, 'dev');
      track('sign_in_code_sent', { newAccount: !!response.data.newAccount });
      return { success: true, sent: true, newAccount: !!response.data.newAccount };
    } catch (err) {
      return failure(err, 'Could not send a code');
    }
  };

  // `check` is { email, code } or { token } from the email link.
  const verifyCode = async (check) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login/verify`, check);
      return signIn(response.data, check.token ? 'link' : 'code');
    } catch (err) {
      track('sign_in_failed', { method: check.token ? 'link' : 'code' });
      return failure(err, 'Sign in failed');
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.admin || false;
  const isActive = user?.isActive || false;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isActive,
    requestCode,
    verifyCode,
    logout,
    getToken,
    refreshUser: fetchUser,
    startGoogleLogin,
    loginWithGoogleCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
